import { WritableDraft } from '@direct-frontend/modules-store';
import { deepDiff, get } from '@direct-frontend/stdlib';
import { getFieldIdByPath } from './getFieldIdByPath';
import { FormSliceState } from '../types';
import { getFieldPathById } from './getFieldPathByFieldId';

/**
 * Обновляет modifiedAt у поля, и всех его родителей
 *
 * Повторно не вызывается, если в visitedNodes есть fieldId поля
 */
export function setParentFieldsModifiedAt({
    form,
    fieldId,
    timestamp,
    visitedNodes,
}: {
    /**
     * Драфт формы
     */
    form: WritableDraft<FormSliceState['forms'][string]>;
    /**
     * ID поля
     */
    fieldId: string;
    /**
     * Время изменения поля
     */
    timestamp: number;
    /**
     * Множество fieldId посещенных полей
     */
    visitedNodes: Set<string>;
}) {
    if (visitedNodes.has(fieldId)) {
        return;
    }

    const fieldState = form.fieldsState[fieldId];
    if (!fieldState) return;
    visitedNodes.add(fieldId);
    fieldState.modifiedAt = timestamp;
    if (fieldState.parentId) {
        setParentFieldsModifiedAt({
            form,
            fieldId: fieldState.parentId,
            timestamp,
            visitedNodes,
        });
    }
}

/**
 * Обновляет modifiedAt у поля, всех его родителей и дочерних полей,
 * действует с учетом deepDiff исходного и текущего состояния поля
 */
export function setFieldModifiedAt({
    form,
    fieldId,
    originalFormValue,
    currentFormValue,
}: {
    /**
     * Драфт формы
     */
    form: WritableDraft<FormSliceState['forms'][string]>;
    /**
     * ID поля
     */
    fieldId: string;
    /**
     * Значение формы до изменения
     */
    originalFormValue: unknown;
    /**
     * Значение формы после изменения
     */
    currentFormValue: unknown;
}) {
    const visitedNodes = new Set<string>();

    const timestamp = Date.now();

    const valuePath = getFieldPathById(() => form.fieldsState, fieldId);

    const originalFieldValue = get(originalFormValue, valuePath);
    const currentFieldValue = get(currentFormValue, valuePath);

    // Получаем дифф реально изменившихся полей дерева
    const deepDiffResult = deepDiff(originalFieldValue, currentFieldValue)?.map(diff => {
        const pathFromRoot = [...valuePath, ...(diff.path ?? [])];

        return {
            ...diff,
            pathFromRoot,
            fieldId: getFieldIdByPath(() => form.fieldsState, pathFromRoot),
        };
    });

    if (deepDiffResult?.length) {
        for (const deepDiffItem of deepDiffResult) {
            setParentFieldsModifiedAt({ form, fieldId: deepDiffItem.fieldId, timestamp, visitedNodes });
        }
    }
}
